/**
 * "Suggest" panel (tracker item 41, phase 2): the top technician + date +
 * time slots for one job from POST /dispatch/suggestions, each with its
 * extra travel and plain-language reasons. "Use this" hands the slot to the
 * Schedule dialog, which saves it with requireNoConflicts (nothing is applied
 * from here). Office roles only (the dispatch page is route-gated).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';
import { formatDayHeading } from '../pages/dispatchUi';
import { addedTravelText, candidateTimeText, excludedText, suggestionReasonText } from '../pages/suggestionUi';
import { Toast } from './Toast';

export interface SuggestPanelJob {
  id: string;
  customerName: string;
  address: string;
  scheduledDate: string | null;
  assignedTo: string | null;
  assignedToEmail?: string | null;
}

interface SuggestPanelProps {
  job: SuggestPanelJob;
  api: Pick<IDispatchApi, 'getJobSuggestions'>;
  /** Shown above the list (e.g. "That slot was just taken") */
  notice?: string | null;
  onUse: (candidate: types.SuggestionCandidate) => void;
  onClose: () => void;
}

export function SuggestPanel({ job, api, notice, onUse, onClose }: SuggestPanelProps) {
  const [anyTechnician, setAnyTechnician] = useState(false);
  const [anyDate, setAnyDate] = useState(false);
  const [result, setResult] = useState<types.SuggestionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await api.getJobSuggestions({ jobId: job.id, anyTechnician, anyDate }));
    } catch (err) {
      setResult(null);
      setError(getErrorMessage(err, 'Could not load suggestions'));
    } finally {
      setLoading(false);
    }
  }, [api, job.id, anyTechnician, anyDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    return () => origin?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const candidates = result?.candidates ?? [];
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="suggest-panel-title"
        className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-xl sm:rounded-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id="suggest-panel-title" className="text-lg font-semibold text-gray-900">Suggested slots</h2>
            <p className="break-words text-sm text-gray-600">{job.customerName} · {job.address}</p>
          </div>
          <button ref={closeButton} type="button" className={secondaryButtonClass} onClick={onClose}>Close</button>
        </div>

        <fieldset className="mt-3 flex flex-wrap gap-4 text-sm text-gray-700">
          <legend className="sr-only">Search wider</legend>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={anyTechnician} disabled={loading} onChange={(event) => setAnyTechnician(event.target.checked)} />
            Any technician{job.assignedTo ? ` (not only ${job.assignedToEmail ?? 'the assigned one'})` : ''}
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={anyDate} disabled={loading} onChange={(event) => setAnyDate(event.target.checked)} />
            Any date{job.scheduledDate ? ` (not only ${formatDayHeading(job.scheduledDate)})` : ''}
          </label>
        </fieldset>

        {notice && <Toast kind="info">{notice}</Toast>}
        {error && <Toast kind="error">{error}</Toast>}
        {loading && <p role="status" className="mt-4 text-sm text-gray-600">Finding slots…</p>}
        {!loading && result && (
          <>
            <p className="mt-3 text-xs text-gray-500">
              {result.from === result.to ? formatDayHeading(result.from) : `${formatDayHeading(result.from)} to ${formatDayHeading(result.to)}`}
              {' · '}Travel is estimated from straight-line distance. Map data © OpenStreetMap contributors.
            </p>
            {result.truncated && <p className="mt-2 text-xs text-amber-800">Only the first 50 technicians were checked.</p>}
            {candidates.length === 0 ? (
              <p className="mt-4 rounded bg-gray-50 p-3 text-sm text-gray-700" data-suggest-empty>{excludedText(result.excluded)}</p>
            ) : (
              <ol className="mt-3 space-y-3" aria-label="Suggestions">
                {candidates.map((candidate) => (
                  <li key={`${candidate.technicianId}-${candidate.date}`} className="rounded border border-gray-200 p-3" data-suggestion={`${candidate.technicianId} ${candidate.date} ${candidate.startTime}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="break-words font-semibold text-gray-900">{candidate.technicianEmail}</div>
                        <div className="text-sm text-gray-700">{formatDayHeading(candidate.date)} · {candidateTimeText(candidate)}</div>
                        <div className="text-xs font-medium text-gray-600">{addedTravelText(candidate)}</div>
                      </div>
                      <button type="button" className={primaryButtonClass} onClick={() => onUse(candidate)}
                        aria-label={`Use ${candidate.technicianEmail}, ${formatDayHeading(candidate.date)} ${candidateTimeText(candidate)}`}>
                        Use this
                      </button>
                    </div>
                    {candidate.reasons.length > 0 && (
                      <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-gray-600">
                        {candidate.reasons.map((reason, index) => <li key={index}>{suggestionReasonText(reason)}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>
    </div>
  );
}
